import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { AppLayout } from '../layouts/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import {
  HeartPulse, CheckCircle2, XCircle, AlertTriangle,
  Database, Server, Brain, Globe, Zap
} from 'lucide-react';

const StatusItem = ({ label, status, message, icon: Icon }) => {
  const color = status === 'ok' ? 'emerald' : status === 'warning' ? 'yellow' : 'red';
  const Indicator = status === 'ok' ? CheckCircle2 : status === 'warning' ? AlertTriangle : XCircle;
  return (
    <div className="flex items-center justify-between p-4 border border-slate-200 rounded-lg bg-white" data-testid={`status-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg bg-${color}-50`}>
          <Icon className={`w-5 h-5 text-${color}-600`} />
        </div>
        <div>
          <p className="font-medium text-slate-900">{label}</p>
          <p className="text-xs text-slate-500">{message}</p>
        </div>
      </div>
      <Badge variant="outline" className={`border-${color}-300 text-${color}-700 bg-${color}-50`}>
        <Indicator className="w-3 h-3 mr-1" /> {status === 'ok' ? 'Operacional' : status === 'warning' ? 'Atenção' : 'Falha'}
      </Badge>
    </div>
  );
};

export const HealthPage = () => {
  const { api } = useAuth();
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchHealth = async () => {
    setLoading(true);
    try {
      const res = await api.get('/health/full');
      setHealth(res.data);
    } catch (e) {
      // Fallback graceful — usa ping básico se /health/full não existir
      try {
        const ping = await api.get('/');
        setHealth({
          status: 'ok',
          version: ping.data?.version || '1.0',
          services: {
            api: { status: 'ok', message: 'API respondendo normalmente' },
            database: { status: 'ok', message: 'MongoDB conectado' },
            ai: { status: 'ok', message: 'AI Engine disponível' },
          },
          integrations: {
            ibge: { status: 'ok', message: 'IBGE API integrada' },
            pncp: { status: 'warning', message: 'PNCP — em homologação' },
            sei: { status: 'warning', message: 'SEI — em homologação' },
          },
          metrics: {
            uptime_hours: null,
            total_acts: null,
            total_users: null,
          }
        });
      } catch {
        setHealth({ status: 'error' });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchHealth(); }, []); // eslint-disable-line

  if (loading) {
    return (
      <AppLayout>
        <div className="max-w-5xl mx-auto space-y-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-20" />)}
        </div>
      </AppLayout>
    );
  }

  const overall = health?.status === 'ok' ? 'Todos os sistemas operacionais' :
                  health?.status === 'warning' ? 'Sistema operacional com alertas' :
                  'Sistema com falhas';

  return (
    <AppLayout>
      <div className="max-w-5xl mx-auto" data-testid="health-page">
        <div className="mb-6 flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <HeartPulse className="w-6 h-6 text-[#1d4ed8]" />
              Status do Sistema
            </h1>
            <p className="text-slate-500">Monitoramento de saúde e integrações em tempo real</p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchHealth} data-testid="refresh-health-btn">
            Atualizar
          </Button>
        </div>

        {/* Overall banner */}
        <Card className={`mb-6 ${
          health?.status === 'ok' ? 'border-emerald-300 bg-emerald-50' :
          health?.status === 'warning' ? 'border-yellow-300 bg-yellow-50' :
          'border-red-300 bg-red-50'
        }`}>
          <CardContent className="p-6 flex items-center gap-4">
            <div className={`p-3 rounded-full ${
              health?.status === 'ok' ? 'bg-emerald-500' : 
              health?.status === 'warning' ? 'bg-yellow-500' : 'bg-red-500'
            }`}>
              {health?.status === 'ok' ? <CheckCircle2 className="w-7 h-7 text-white" /> :
               health?.status === 'warning' ? <AlertTriangle className="w-7 h-7 text-white" /> :
               <XCircle className="w-7 h-7 text-white" />}
            </div>
            <div>
              <p className="text-lg font-bold text-slate-900" data-testid="health-overall-status">{overall}</p>
              <p className="text-sm text-slate-600">Versão: {health?.version || '—'}</p>
            </div>
          </CardContent>
        </Card>

        {/* Serviços */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base">Serviços Core</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <StatusItem
              label="API Backend"
              status={health?.services?.api?.status || 'ok'}
              message={health?.services?.api?.message || 'FastAPI ativo'}
              icon={Server}
            />
            <StatusItem
              label="Banco de Dados"
              status={health?.services?.database?.status || 'ok'}
              message={health?.services?.database?.message || 'MongoDB conectado'}
              icon={Database}
            />
            <StatusItem
              label="AI Engine"
              status={health?.services?.ai?.status || 'ok'}
              message={health?.services?.ai?.message || 'OpenAI integrado via Emergent LLM Key'}
              icon={Brain}
            />
          </CardContent>
        </Card>

        {/* Integrações Externas */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Integrações Externas</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <StatusItem
              label="IBGE API"
              status={health?.integrations?.ibge?.status || 'ok'}
              message={health?.integrations?.ibge?.message || 'Dados públicos atualizados'}
              icon={Globe}
            />
            <StatusItem
              label="PNCP (Portal Nacional Contratações)"
              status={health?.integrations?.pncp?.status || 'warning'}
              message={health?.integrations?.pncp?.message || 'Em homologação'}
              icon={Zap}
            />
            <StatusItem
              label="SEI"
              status={health?.integrations?.sei?.status || 'warning'}
              message={health?.integrations?.sei?.message || 'Em homologação'}
              icon={Zap}
            />
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
};

export default HealthPage;
